import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CreditCardIcon,
  PresentationChartLineIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { LineChart } from '../charts/Chart';

/**
 * Advanced Cash Flow & Financial Predictor
 * Comprehensive financial analytics with AI-powered predictions
 */
const CashFlowPredictor = () => {
  const [cashFlowData, setCashFlowData] = useState({});
  const [predictions, setPredictions] = useState([]);
  const [financialAlerts, setFinancialAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateCashFlowAnalysis();
    
    // Update every 2 minutes
    const interval = setInterval(generateCashFlowAnalysis, 120000);
    return () => clearInterval(interval);
  }, []);

  const generateCashFlowAnalysis = async () => {
    setLoading(true);
    
    try {
      // Fetch real cash flow prediction data from API
      const response = await axios.get('/api/admin/dashboard/cash-flow-prediction');
      const data = response.data;
      
      setCashFlowData(data);
      setPredictions(data.projections || []);
      setFinancialAlerts(data.alerts || []);
      
    } catch (error) {
      console.error('Error fetching cash flow prediction data:', error);
      // Fall back to mock data if API fails
      
      // Generate comprehensive cash flow data
      const mockCashFlowData = {
      current: {
        cashOnHand: 485000,
        monthlyIncome: 289000,
        monthlyExpenses: 234000,
        netCashFlow: 55000,
        burnRate: 234000,
        runwayMonths: 2.07,
        operatingCashFlow: 67000,
        freeCashFlow: 41000
      },
      
      projections: Array.from({ length: 12 }, (_, i) => {
        const month = new Date();
        month.setMonth(month.getMonth() + i);
        const baseIncome = 289000;
        const baseExpense = 234000;
        const seasonalityFactor = 1 + Math.sin(i * Math.PI / 6) * 0.2;
        
        return {
          month: month.toLocaleDateString('he-IL', { month: 'short', year: 'numeric' }),
          income: Math.round(baseIncome * seasonalityFactor),
          expenses: Math.round(baseExpense * (0.95 + Math.random() * 0.1)),
          netCashFlow: 0,
          cumulativeCash: 0
        };
      }),
      
      incomeBreakdown: [
        { source: 'מכירות B2C', amount: 165000, percentage: 57.1, trend: 'up' },
        { source: 'מכירות B2B', amount: 89000, percentage: 30.8, trend: 'up' },
        { source: 'שירותים', amount: 25000, percentage: 8.7, trend: 'stable' },
        { source: 'אחר', amount: 10000, percentage: 3.4, trend: 'down' }
      ],
      
      expenseBreakdown: [
        { category: 'רכש מוצרים', amount: 145000, percentage: 62.0, essential: true },
        { category: 'שכר עובדים', amount: 58000, percentage: 24.8, essential: true },
        { category: 'שיווק ופרסום', amount: 18000, percentage: 7.7, essential: false },
        { category: 'תפעול ומשרד', amount: 13000, percentage: 5.5, essential: true }
      ],
      
      scenarioAnalysis: [
        {
          name: 'תרחיש אופטימי',
          probability: 30,
          income: 145,  // % of current
          expenses: 95,
          netCashFlow: 89500,
          description: 'צמיחה של 45% בהכנסות, יעילות תפעולית'
        },
        {
          name: 'תרחיש בסיסי',
          probability: 50,
          income: 110,
          expenses: 105,
          netCashFlow: 26900,
          description: 'צמיחה מתונה של 10%, עלייה קלה בעלויות'
        },
        {
          name: 'תרחיש פסימי',
          probability: 20,
          income: 80,
          expenses: 110,
          netCashFlow: -25740,
          description: 'ירידה של 20% בהכנסות, עלייה בעלויות'
        }
      ],
      
      cashFlowTrends: Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (29 - i));
        return {
          label: date.toLocaleDateString('he-IL', { month: 'short', day: 'numeric' }),
          value: 485000 + (Math.random() - 0.5) * 50000 + i * 1000
        };
      }),
      
      paymentTermsAnalysis: {
        averageCollection: 28,
        averagePayment: 45,
        workingCapitalDays: 17,
        accountsReceivable: 156000,
        accountsPayable: 89000,
        dso: 28, // Days Sales Outstanding
        dpo: 45  // Days Payable Outstanding
      }
    };

    // Calculate net cash flow and cumulative for projections
    let cumulativeCash = mockCashFlowData.current.cashOnHand;
    mockCashFlowData.projections = mockCashFlowData.projections.map(projection => {
      const netCashFlow = projection.income - projection.expenses;
      cumulativeCash += netCashFlow;
      return {
        ...projection,
        netCashFlow,
        cumulativeCash
      };
    });

    setCashFlowData(mockCashFlowData);
    
    // Generate predictions
    setPredictions([
      {
        type: 'positive',
        title: 'צמיחה בהכנסות צפויה',
        description: 'בהתבסס על מגמות שוק, צמיחה של 25% בהכנסות צפויה ב-3 החודשים הבאים',
        confidence: 78,
        timeframe: '3 חודשים',
        impact: 72500
      },
      {
        type: 'warning',
        title: 'לחץ על תזרים מזומנים',
        description: 'תזרים המזומנים יהיה דחוק ביולי-אוגוסט עקב תשלומי ספקים גדולים',
        confidence: 84,
        timeframe: '4-5 חודשים',
        impact: -45000
      },
      {
        type: 'opportunity',
        title: 'הזדמנות השקעה',
        description: 'תקופה אידיאלית להשקעה בפרסום ומלאי לקראת עונת החגים',
        confidence: 71,
        timeframe: '2 חודשים',
        impact: 125000
      }
    ]);
    
    // Generate financial alerts
    setFinancialAlerts([
      {
        id: 1,
        type: 'critical',
        title: 'מסלול נזילות קצר',
        message: 'המזומנים יספיקו רק ל-2.07 חודשים בקצב הנוכחי',
        action: 'הגדל הכנסות או צמצם הוצאות',
        priority: 1
      },
      {
        id: 2,
        type: 'warning',
        title: 'איחור בגבייה',
        message: 'גבייה ממוצעת של 28 ימים - 8 ימים מעל הממוצע',
        action: 'שפר תהליכי גבייה',
        priority: 2
      },
      {
        id: 3,
        type: 'info',
        title: 'מועד תשלום לספקים',
        message: '89,000₪ לתשלום לספקים בעוד 5 ימים',
        action: 'הכן תזרים מזומנים',
        priority: 3
      }
    ]);
    
    }
    
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '₪0';
    }
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount));
  };

  const getPredictionColor = (type) => {
    switch (type) {
      case 'positive': return 'bg-green-50 border-green-200 text-green-800';
      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'opportunity': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical': return 'bg-red-50 border-red-500';
      case 'warning': return 'bg-yellow-50 border-yellow-500';
      case 'info': return 'bg-blue-50 border-blue-500';
      default: return 'bg-gray-50 border-gray-500';
    }
  };

  const getHealthScore = (runway) => {
    if (runway >= 6) return { score: 'בריא', color: 'text-green-600', bg: 'bg-green-100' };
    if (runway >= 3) return { score: 'זהירות', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { score: 'קריטי', color: 'text-red-600', bg: 'bg-red-100' };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg border border-gray-200 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const healthScore = getHealthScore(cashFlowData.current?.runwayMonths || 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg mr-3">
            <BanknotesIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">חזאי תזרים מזומנים מתקדם</h3>
            <p className="text-sm text-gray-600">ניתוח פיננסי מקיף עם חיזויים מבוססי AI</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${healthScore.bg} ${healthScore.color}`}>
            {healthScore.score}
          </div>
        </div>
      </div>

      {/* Financial Alerts */}
      {financialAlerts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-md font-semibold text-gray-900 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 ml-2" />
            התראות פיננסיות
          </h4>
          <div className="space-y-2">
            {financialAlerts.sort((a, b) => a.priority - b.priority).map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border-r-4 ${getAlertColor(alert.type)}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-gray-900">{alert.title}</h5>
                    <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                  </div>
                  <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors">
                    {alert.action}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">מזומנים זמינים</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(cashFlowData.current?.cashOnHand)}</p>
              <p className="text-sm text-gray-500 mt-1">נזילות מיידית</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <BanknotesIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">תזרים נקי</p>
              <p className={`text-2xl font-bold ${cashFlowData.current?.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {cashFlowData.current?.netCashFlow >= 0 ? '+' : ''}{formatCurrency(cashFlowData.current?.netCashFlow)}
              </p>
              <p className="text-sm text-gray-500 mt-1">לחודש</p>
            </div>
            <div className={`p-3 rounded-full ${cashFlowData.current?.netCashFlow >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {cashFlowData.current?.netCashFlow >= 0 ? 
                <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" /> : 
                <ArrowTrendingDownIcon className="h-8 w-8 text-red-600" />
              }
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">מסלול נזילות</p>
              <p className={`text-2xl font-bold ${cashFlowData.current?.runwayMonths >= 6 ? 'text-green-600' : cashFlowData.current?.runwayMonths >= 3 ? 'text-yellow-600' : 'text-red-600'}`}>
                {cashFlowData.current?.runwayMonths ? cashFlowData.current.runwayMonths.toFixed(1) : '0.0'}
              </p>
              <p className="text-sm text-gray-500 mt-1">חודשים</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <ClockIcon className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">תזרים תפעולי</p>
              <p className="text-2xl font-bold text-green-600">+{formatCurrency(cashFlowData.current?.operatingCashFlow)}</p>
              <p className="text-sm text-gray-500 mt-1">מפעילות עסקית</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <PresentationChartLineIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* AI Predictions */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
        <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
          <PresentationChartLineIcon className="h-5 w-5 text-blue-600 ml-2" />
          חיזויים פיננסיים מבוססי AI
        </h4>
        <div className="space-y-4">
          {predictions.map((prediction, index) => (
            <div key={index} className={`p-4 rounded-lg border ${getPredictionColor(prediction.type)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h5 className="font-semibold mb-1">{prediction.title}</h5>
                  <p className="text-sm mb-3">{prediction.description}</p>
                  <div className="flex items-center space-x-4 space-x-reverse text-sm">
                    <span>ביטחון: {prediction.confidence}%</span>
                    <span>זמן: {prediction.timeframe}</span>
                    <span className={`font-medium ${prediction.impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      השפעה: {prediction.impact > 0 ? '+' : ''}{formatCurrency(prediction.impact)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cash Flow Trend */}
        <LineChart
          data={cashFlowData.cashFlowTrends || []}
          title="מגמת תזרים מזומנים (30 ימים)"
          color="#059669"
          height={300}
        />

        {/* 12-Month Projections */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4">תחזית 12 חודשים</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {cashFlowData.projections?.map((month, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-900">{month.month}</p>
                  <p className="text-sm text-gray-600">
                    הכנסות: {formatCurrency(month.income)} • הוצאות: {formatCurrency(month.expenses)}
                  </p>
                </div>
                <div className="text-left">
                  <div className={`text-sm font-bold ${month.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {month.netCashFlow >= 0 ? '+' : ''}{formatCurrency(month.netCashFlow)}
                  </div>
                  <div className="text-xs text-gray-500">
                    מצטבר: {formatCurrency(month.cumulativeCash)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Income & Expense Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Sources */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
            <ArrowUpIcon className="h-5 w-5 text-green-500 ml-2" />
            מקורות הכנסה
          </h4>
          <div className="space-y-3">
            {cashFlowData.incomeBreakdown?.map((source, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{source.source}</p>
                  <div className="flex items-center mt-1">
                    <div className="w-20 bg-gray-200 rounded-full h-2 ml-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${source.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{source.percentage ? source.percentage.toFixed(1) : '0.0'}%</span>
                  </div>
                </div>
                <div className="text-left ml-4">
                  <div className="font-bold text-green-600">{formatCurrency(source.amount)}</div>
                  <div className="text-xs text-gray-500 flex items-center">
                    {source.trend === 'up' ? <ArrowUpIcon className="h-3 w-3 text-green-500 ml-1" /> :
                     source.trend === 'down' ? <ArrowDownIcon className="h-3 w-3 text-red-500 ml-1" /> :
                     <div className="w-3 h-3 rounded-full bg-gray-400 ml-1" />}
                    מגמה
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
            <ArrowDownIcon className="h-5 w-5 text-red-500 ml-2" />
            פילוח הוצאות
          </h4>
          <div className="space-y-3">
            {cashFlowData.expenseBreakdown?.map((expense, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center">
                    <p className="font-semibold text-gray-900">{expense.category}</p>
                    {expense.essential && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full mr-2">
                        חיוני
                      </span>
                    )}
                  </div>
                  <div className="flex items-center mt-1">
                    <div className="w-20 bg-gray-200 rounded-full h-2 ml-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full" 
                        style={{ width: `${expense.percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{expense.percentage ? expense.percentage.toFixed(1) : '0.0'}%</span>
                  </div>
                </div>
                <div className="text-left ml-4">
                  <div className="font-bold text-red-600">{formatCurrency(expense.amount)}</div>
                  <div className="text-xs text-gray-500">
                    {expense.essential ? 'קבוע' : 'משתנה'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scenario Analysis */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
          <CalendarIcon className="h-5 w-5 text-indigo-500 ml-2" />
          ניתוח תרחישים
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cashFlowData.scenarioAnalysis?.map((scenario, index) => (
            <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-gray-900">{scenario.name}</h5>
                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                  {scenario.probability}% סיכוי
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-4">{scenario.description}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">הכנסות:</span>
                  <span className="font-medium">{scenario.income}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">הוצאות:</span>
                  <span className="font-medium">{scenario.expenses}%</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">תזרים נקי:</span>
                  <span className={`font-bold ${scenario.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {scenario.netCashFlow >= 0 ? '+' : ''}{formatCurrency(scenario.netCashFlow)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Terms Analysis */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
          <CreditCardIcon className="h-5 w-5 text-purple-500 ml-2" />
          ניתוח תנאי תשלום
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{cashFlowData.paymentTermsAnalysis?.averageCollection}</div>
            <div className="text-sm text-gray-600">ימי גבייה ממוצעים</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{cashFlowData.paymentTermsAnalysis?.averagePayment}</div>
            <div className="text-sm text-gray-600">ימי תשלום לספקים</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{cashFlowData.paymentTermsAnalysis?.workingCapitalDays}</div>
            <div className="text-sm text-gray-600">ימי הון חוזר</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(cashFlowData.paymentTermsAnalysis?.accountsReceivable)}</div>
            <div className="text-sm text-gray-600">חייבים</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashFlowPredictor;
